import { useState } from "react";
import { Button } from "../../Button/Button";
import { LoadingPlaceholder } from "../../LoadingPlaceholder/LoadingPlaceholder";
import { Modal } from "../../Modal/Modal";
import { Body } from "../../Typography/Body";
import "./ChatReferenceImage.css";

export interface ChatReferenceImageProps {
  imageUrl?: string;
  loading: boolean;
  caption: string;
  altText: string;
  sourceTitle: string;
  sourceUrl: string;
  onRetry: () => void;
  onLoadError?: () => void;
}

/** A sourced reference in a chat response, with the source retained on failure. */
export function ChatReferenceImage({
  imageUrl,
  loading,
  caption,
  altText,
  sourceTitle,
  sourceUrl,
  onRetry,
  onLoadError,
}: ChatReferenceImageProps) {
  const [expanded, setExpanded] = useState(false);
  const [failedUrl, setFailedUrl] = useState<string>();
  const [loadedUrl, setLoadedUrl] = useState<string>();
  const failed = !!imageUrl && failedUrl === imageUrl;
  const loaded = !!imageUrl && loadedUrl === imageUrl;

  const unavailable = failed || (!loading && !imageUrl);
  const source = (
    <a
      className="indigo-chat-reference-image__source"
      href={sourceUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      Source: {sourceTitle}
    </a>
  );

  return (
    <figure
      className="indigo-chat-reference-image w-full max-w-sm"
      data-testid="chat-reference-image"
    >
      <Body as="span" size="xs">
        Reference image
      </Body>
      {!unavailable && (loading || !loaded) && (
        <div role="status" aria-label="Loading reference image">
          <LoadingPlaceholder size="sm" noLogo />
        </div>
      )}
      {imageUrl && !failed && (
        <button
          type="button"
          className="indigo-chat-reference-image__preview"
          onClick={() => setExpanded(true)}
          aria-label={`Expand reference image: ${altText}`}
        >
          <img
            src={imageUrl}
            alt={altText}
            referrerPolicy="no-referrer"
            onLoad={() => setLoadedUrl(imageUrl)}
            onError={() => {
              setFailedUrl(imageUrl);
              onLoadError?.();
            }}
          />
        </button>
      )}
      {unavailable && (
        <div className="indigo-chat-reference-image__error" role="status">
          <Body size="sm">
            Image unavailable. You can still open the source.
          </Body>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFailedUrl(undefined);
              setLoadedUrl(undefined);
              onRetry();
            }}
          >
            Retry image
          </Button>
        </div>
      )}
      <figcaption>
        <Body size="sm">{caption}</Body>
        <Body size="xs">{source}</Body>
      </figcaption>
      <Modal
        open={expanded}
        onCancel={() => setExpanded(false)}
        centered
        className="indigo-chat-reference-image-modal"
        footer={
          <div className="indigo-chat-reference-image-modal__caption">
            <Body size="sm">{caption}</Body>
            <Body size="xs">{source}</Body>
          </div>
        }
        title={
          <Body as="span" size="md" className="font-semibold">
            Reference image
          </Body>
        }
        width="min(56rem, calc(100vw - 2 * var(--indigo-spacing-md)))"
      >
        <div className="indigo-chat-reference-image-modal__stage">
          <img
            className="indigo-chat-reference-image__expanded"
            src={imageUrl}
            alt={altText}
            referrerPolicy="no-referrer"
          />
        </div>
      </Modal>
    </figure>
  );
}
